<template>
  <v-row no-gutters class="ps-wizard">
    <!-- Stepper vertical sticky -->
    <v-col cols="12" md="3" class="ps-stepper-col pa-6">
      <router-link
        to="/mis-productos"
        class="ps-back mb-6 d-block font-weight-bold"
      >
        <v-icon small>mdi-chevron-left</v-icon> Volver al listado
      </router-link>

      <h2 class="ps-wizard__title mb-2">Nuevo producto</h2>
      <p class="ps-wizard__hint mb-6">
        Configura tu producto en {{ steps.length }} pasos. Podrás editarlo después en su detalle.
      </p>

      <div
        v-for="(step, i) in steps"
        :key="i"
        class="ps-step"
        :class="{
          'ps-step--active': currentStep === i,
          'ps-step--done': currentStep > i,
        }"
        @click="goToStep(i)"
      >
        <div class="ps-step__bullet">
          <v-icon v-if="currentStep > i" color="white" size="18">mdi-check</v-icon>
          <span v-else>{{ i + 1 }}</span>
        </div>
        <div>
          <div class="ps-step__title">{{ step.title }}</div>
          <div class="ps-step__subtitle">{{ step.subtitle }}</div>
        </div>
      </div>
    </v-col>

    <!-- Contenido -->
    <v-col cols="12" md="9" class="ps-content pa-6 pa-md-10">
      <v-sheet class="rounded-xl pa-6 pa-md-8" elevation="1">
        <!-- Paso 1: Identificación -->
        <div v-if="currentStep === 0">
          <h3 class="ps-step-title">Identifica tu producto</h3>
          <p class="ps-step-desc">Define cómo lo encontrarán tus miembros.</p>

          <v-form ref="form0" v-model="stepValid[0]">
            <v-text-field
              v-model="form.name"
              label="Nombre del producto"
              outlined
              :rules="[v => !!v || 'Requerido', v => (v && v.length <= 80) || 'Máximo 80 caracteres']"
              counter="80"
            />
            <v-textarea
              v-model="form.shortDescription"
              label="Descripción corta"
              outlined
              auto-grow
              rows="2"
              :rules="[v => (v || '').length <= 160 || 'Máximo 160 caracteres']"
              counter="160"
              hint="Aparece debajo del nombre en el listado público."
              persistent-hint
            />
          </v-form>
        </div>

        <!-- Paso 2: Descripción larga -->
        <div v-else-if="currentStep === 1">
          <h3 class="ps-step-title">Describe a detalle</h3>
          <p class="ps-step-desc">Comunica los beneficios y qué incluye.</p>

          <v-textarea
            v-model="form.description"
            label="Descripción completa"
            outlined
            auto-grow
            rows="8"
            hint="Tip: estructura en párrafos cortos."
            persistent-hint
          />
        </div>

        <!-- Paso 3: Precio y métodos de pago -->
        <div v-else-if="currentStep === 2">
          <h3 class="ps-step-title">Precio y formas de pago</h3>
          <p class="ps-step-desc">Define cuánto cobrarás y cómo aceptarás los pagos.</p>

          <v-form ref="form2" v-model="stepValid[2]">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.price"
                  label="Precio"
                  prefix="$"
                  type="number"
                  min="0"
                  outlined
                  :rules="[v => v !== null && v !== '' || 'Requerido', v => v >= 0 || 'Debe ser ≥ 0']"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.currency"
                  :items="['MXN', 'USD']"
                  label="Moneda"
                  outlined
                />
              </v-col>
            </v-row>

            <div class="ps-step-desc mt-2 mb-3">Métodos de pago aceptados</div>
            <v-row dense>
              <v-col cols="12" sm="6" v-for="method in paymentMethodOptions" :key="method.value">
                <v-checkbox
                  v-model="form.paymentMethods"
                  :value="method.value"
                  :label="method.label"
                  hide-details
                  class="mt-0"
                  color="primaryVariant"
                />
              </v-col>
            </v-row>

            <v-divider class="my-5" />
            <v-switch
              v-if="isDevEnvironment"
              v-model="form.partialPaymentsEnabled"
              label="Permitir pagos parciales (plan de pagos)"
              color="primaryVariant"
              hide-details
            />
          </v-form>
        </div>

        <!-- Paso 4: Banner 16:9 -->
        <div v-else-if="currentStep === 3">
          <h3 class="ps-step-title">Imagen principal</h3>
          <p class="ps-step-desc">Sube una imagen en proporción 16:9 (mín. 1280×720 px).</p>

          <v-file-input
            v-model="bannerFile"
            accept="image/*"
            label="Selecciona una imagen"
            outlined
            prepend-icon="mdi-image"
            show-size
            @change="onBannerSelected"
          />

          <div v-if="bannerPreview" class="mt-4">
            <v-img :src="bannerPreview" :aspect-ratio="16/9" class="rounded-lg" />
          </div>
        </div>

        <!-- Paso 5: Facturación -->
        <div v-else-if="currentStep === 4">
          <h3 class="ps-step-title">Datos de facturación</h3>
          <p class="ps-step-desc">Se usarán para emitir CFDI de las ventas (opcional).</p>

          <v-switch
            v-model="form.billingEnabled"
            label="Habilitar facturación para este producto"
            color="secondary"
            inset
            hide-details
            class="mb-4"
          />

          <v-row v-if="form.billingEnabled">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.billingInformation.productKey"
                label="Clave producto/servicio (SAT)"
                outlined
                hint="Ej. 80131500"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.billingInformation.unitKey"
                label="Clave unidad (SAT)"
                outlined
                hint="Ej. E48"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.billingInformation.unitName"
                label="Nombre unidad"
                outlined
                hint="Ej. Servicio"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.billingInformation.taxType"
                :items="[{ value: 'EXENTO', text: 'Exento' }, { value: 'IVA_16', text: 'IVA 16%' }]"
                label="Tipo de impuesto"
                outlined
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.billingInformation.description"
                label="Descripción para facturación"
                outlined
                rows="2"
                auto-grow
              />
            </v-col>
          </v-row>
          <v-alert v-else type="info" outlined dense class="mb-0">
            La facturación está deshabilitada. Las ventas de este producto no generarán CFDI.
          </v-alert>
        </div>

        <!-- Paso 6: Términos -->
        <div v-else-if="currentStep === 5">
          <h3 class="ps-step-title">Términos y condiciones</h3>
          <p class="ps-step-desc">Información legal que verán los compradores antes de pagar.</p>

          <v-textarea
            v-model="form.termsAndConditions"
            label="Términos y condiciones"
            outlined
            auto-grow
            rows="10"
          />
        </div>

        <!-- Acciones -->
        <v-divider class="my-6" />
        <div class="d-flex flex-wrap" style="gap: 12px;">
          <v-btn
            text
            class="rounded-lg font-weight-bold"
            :color="color.primaryVariant"
            :disabled="currentStep === 0 || saving"
            @click="prev"
          >
            <v-icon left>mdi-chevron-left</v-icon> Anterior
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="currentStep < steps.length - 1"
            depressed
            large
            color="secondary"
            class="rounded-lg font-weight-bold ps-cta"
            :style="{ color: color.primaryVariant }"
            :disabled="!isStepValid"
            @click="next"
          >
            Siguiente <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>

          <v-btn
            v-else
            depressed
            large
            color="secondary"
            class="rounded-lg font-weight-bold ps-cta"
            :style="{ color: color.primaryVariant }"
            :loading="saving"
            :disabled="!canSubmit"
            @click="submit"
          >
            <v-icon left>mdi-check</v-icon> Crear como borrador
          </v-btn>
        </div>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { colors } from "@/constants/colors";
import { isAsofomOrganization } from "@/share/imef";

export default {
  name: "NewProductService",
  data() {
    return {
      color: colors,
      currentStep: 0,
      saving: false,
      bannerFile: null,
      bannerPreview: null,
      stepValid: [false, true, false, true, true, true],
      steps: [
        { title: "Identificación", subtitle: "Nombre y descripción corta" },
        { title: "Descripción", subtitle: "Detalle del producto/servicio" },
        { title: "Precio y pagos", subtitle: "Cuánto y cómo cobrar" },
        { title: "Imagen", subtitle: "Banner 16:9" },
        { title: "Facturación", subtitle: "Datos para CFDI" },
        { title: "Términos", subtitle: "Condiciones de venta" },
      ],
      allPaymentMethodOptions: [
        { value: "CREDIT_CARD", label: "Tarjeta de crédito/débito" },
        { value: "CASH", label: "Efectivo" },
        { value: "BANK_TRANSFER", label: "Transferencia bancaria" },
        { value: "PEIBO", label: "Peibo" },
        { value: "MANUALLY_COMPLETED", label: "Pago manual (admin)" },
      ],
      form: {
        name: "",
        shortDescription: "",
        description: "",
        price: null,
        currency: "MXN",
        paymentMethods: ["CREDIT_CARD"],
        partialPaymentsEnabled: false,
        bannerImage: "",
        billingEnabled: false,
        billingInformation: {
          productKey: "",
          unitKey: "",
          unitName: "",
          description: "",
          taxType: "EXENTO",
        },
        termsAndConditions: "",
      },
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    isAsofom() {
      return isAsofomOrganization(this.user && this.user._id, this.user && this.user.headOffice && this.user.headOffice._id);
    },
    isDevEnvironment() {
      return process.env.VUE_APP_ENVIRONMENT === 'development';
    },
    paymentMethodOptions() {
      if (this.isDevEnvironment || this.isAsofom) return this.allPaymentMethodOptions;
      return this.allPaymentMethodOptions.filter((m) => m.value !== "PEIBO");
    },
    isStepValid() {
      if (this.currentStep === 0) {
        return !!this.form.name && this.form.name.length <= 80 && (this.form.shortDescription || "").length <= 160;
      }
      if (this.currentStep === 2) {
        return this.form.price !== null && this.form.price !== "" && this.form.price >= 0 && this.form.paymentMethods.length > 0;
      }
      return true;
    },
    canSubmit() {
      return !!this.form.name && this.form.price !== null && this.form.price !== "" && this.form.paymentMethods.length > 0;
    },
  },
  methods: {
    ...mapActions("productServices", ["createProductService", "uploadProductServiceBanner"]),
    ...mapMutations("notification", ["show"]),
    next() {
      if (this.currentStep < this.steps.length - 1) this.currentStep++;
    },
    prev() {
      if (this.currentStep > 0) this.currentStep--;
    },
    goToStep(i) {
      if (i < this.currentStep) this.currentStep = i;
    },
    onBannerSelected(file) {
      if (!file) {
        this.bannerPreview = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.bannerPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async submit() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        const res = await this.createProductService(payload);
        if (!res.status) {
          this.show({ text: "No se pudo crear el producto.", color: "error" });
          this.saving = false;
          return;
        }
        const created = res.data;

        if (this.bannerFile) {
          await this.uploadProductServiceBanner({ id: created._id, file: this.bannerFile });
        }

        this.show({ text: "Producto creado en borrador.", color: "success" });
        this.$router.push(`/mi-producto-servicio/${created._id}`).catch(() => {});
      } catch (err) {
        this.show({ text: err.message || "Error al crear producto.", color: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ps-wizard {
  min-height: 100vh;
  background: #f5f6f8;
}
.ps-stepper-col {
  background: #ffffff;
  border-right: 1px solid #e8eaef;
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
}
.ps-back {
  color: #1b273e;
  text-decoration: none;
  font-size: 13px;
}
.ps-wizard__title {
  color: #1b273e;
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.3px;
}
.ps-wizard__hint {
  color: #5a6478;
  font-size: 13px;
}
.ps-step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.ps-step:hover {
  background: #f5f6f8;
}
.ps-step__bullet {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e8eaef;
  color: #5a6478;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex: 0 0 28px;
}
.ps-step--active .ps-step__bullet {
  background: #ffb500;
  color: #1b273e;
}
.ps-step--done .ps-step__bullet {
  background: #1b273e;
  color: #ffffff;
}
.ps-step__title {
  color: #1b273e;
  font-weight: 600;
  font-size: 14px;
}
.ps-step__subtitle {
  color: #7a8294;
  font-size: 12px;
}
.ps-content {
  background: #f5f6f8;
}
.ps-step-title {
  color: #1b273e;
  font-weight: 700;
  font-size: 22px;
  margin-bottom: 4px;
}
.ps-step-desc {
  color: #5a6478;
  font-size: 14px;
  margin-bottom: 24px;
}
.ps-cta {
  text-transform: none;
  letter-spacing: 0;
}
</style>
